import type { OptimizerAuditV2Record } from '../../types/audit.types';
import { formatAuditAccountType } from '../presentation/accountTypeLabels';
import { isRegisteredAccountType } from '../presentation/isRegisteredAccountType';
import type { ReportSubAccountDisplayMetadata } from '../snapshot/buildReportDisplayMetadata';
import { buildSubAccountNamesById } from './formatAdvisorInsightText';

export type TaxLocationAccountFlow = {
  fromAccount: string;
  toAccount: string;
  amountCents: number;
};

const TAX_LOCATION_APPLIED_PATTERN = /Tax-location penalty applied/i;

export const hasTaxLocationApplied = (
  audit: OptimizerAuditV2Record | undefined,
): boolean =>
  (audit?.decisions ?? []).some((decision) =>
    TAX_LOCATION_APPLIED_PATTERN.test(decision.action ?? ''),
  );

const resolveAccountLabel = (
  subAccount: NonNullable<OptimizerAuditV2Record['subAccountResults']>[number],
  subAccountsById: Map<string, ReportSubAccountDisplayMetadata>,
): { label: string; isRegistered: boolean } => {
  const subAccountId = subAccount.subAccountId ?? '';
  const fromReport = subAccountsById.get(subAccountId);
  const accountType = formatAuditAccountType(subAccount.accountType);

  return {
    label: fromReport?.name ?? accountType,
    isRegistered: isRegisteredAccountType(subAccount.accountType),
  };
};

/** Pair net cross-account buy/sell deltas into from→to reallocation flows. */
export const buildTaxLocationAccountFlows = (
  audit: OptimizerAuditV2Record | undefined,
  subAccountsById: Map<string, ReportSubAccountDisplayMetadata> = new Map(),
  limit = 4,
): TaxLocationAccountFlow[] => {
  if (!audit || !hasTaxLocationApplied(audit)) {
    return [];
  }

  const accountNames = buildSubAccountNamesById(subAccountsById, audit);
  type AccountNet = {
    label: string;
    isRegistered: boolean;
    netCents: number;
  };

  const accountNets = new Map<string, AccountNet>();

  const subAccountResults = audit.subAccountResults ?? [];

  for (let index = 0; index < subAccountResults.length; index += 1) {
    const subAccount = subAccountResults[index];
    const { label, isRegistered } = resolveAccountLabel(
      subAccount,
      accountNames,
    );
    const netCents = (subAccount.weightDiffs ?? []).reduce(
      (sum, diff) => sum + (diff.deltaCents ?? 0),
      0,
    );

    if (netCents === 0) {
      continue;
    }

    // Key by the unique sub-account id (not the display label): two sleeves
    // that resolve to the same label must not overwrite each other's net.
    const key = subAccount.subAccountId || `__index_${index}`;
    accountNets.set(key, { label, isRegistered, netCents });
  }

  const sells = [...accountNets.values()]
    .filter((entry) => entry.netCents < 0)
    .map((entry) => ({
      ...entry,
      remaining: Math.abs(entry.netCents),
    }));
  const buys = [...accountNets.values()]
    .filter((entry) => entry.netCents > 0)
    .map((entry) => ({
      ...entry,
      remaining: entry.netCents,
    }));

  sells.sort((a, b) => Number(a.isRegistered) - Number(b.isRegistered));
  buys.sort((a, b) => Number(b.isRegistered) - Number(a.isRegistered));

  const flowTotals = new Map<string, number>();

  for (const sell of sells) {
    for (const buy of buys) {
      if (
        sell.label === buy.label ||
        sell.remaining <= 0 ||
        buy.remaining <= 0
      ) {
        continue;
      }

      const amountCents = Math.min(sell.remaining, buy.remaining);
      sell.remaining -= amountCents;
      buy.remaining -= amountCents;

      const key = `${sell.label}→${buy.label}`;
      flowTotals.set(key, (flowTotals.get(key) ?? 0) + amountCents);
    }
  }

  return [...flowTotals.entries()]
    .map(([key, amountCents]) => {
      const [fromAccount, toAccount] = key.split('→');

      return {
        fromAccount,
        toAccount,
        amountCents,
      };
    })
    .filter((flow) => flow.amountCents > 0)
    .sort((a, b) => b.amountCents - a.amountCents)
    .slice(0, limit);
};
